import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";

const BloodGroupGenderBarChart = ({ bloodGroupGenderStatistics, width = 433, height = 251 }) => {
  const { t } = useTranslation();
  const data = bloodGroupGenderStatistics;

  const chartData = useMemo(() => {
    if (!data || !data.details) return null;

    const allBloodGroups = Array.from(
      new Set(
        data.details
          .flatMap((g) => g.bloodGroups.map((bg) => bg.bloodType))
          .sort()
      )
    );

    const maleData =
      data.details.find((g) => g.gender === "MALE")?.bloodGroups || [];
    const femaleData =
      data.details.find((g) => g.gender === "FEMALE")?.bloodGroups || [];

    const maleCounts = allBloodGroups.map(
      (type) => maleData.find((b) => b.bloodType === type)?.count || 0
    );
    const femaleCounts = allBloodGroups.map(
      (type) => femaleData.find((b) => b.bloodType === type)?.count || 0
    );

    return {
      labels: allBloodGroups,
      datasets: [
        {
          label: t("Male"),
          data: maleCounts,
          backgroundColor: "#3b4cb8",
        },
        {
          label: t("Female"),
          data: femaleCounts,
          backgroundColor: "rgba(255, 99, 132, 0.7)",
        },
      ],
    };
  }, [data, t]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: "top",
        labels: { color: "#999", font: { size: 14 } },
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const raw = context.raw;
            const total = data?.totalDonors || 0;
            const percentage = total > 0 ? ((raw / total) * 100).toFixed(2) : 0;
            return `${context.dataset.label}: ${raw} donors (${percentage}%)`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { color: "#999" },
        title: { display: true, text: t("Number of Donors"), color: "#666" },
      },
      x: {
        ticks: { color: "#999" },
        title: { display: true, text: t("Blood Group"), color: "#666" },
      },
    },
  };

  if (!chartData)
    return <p className="text-center mt-3">{t("No chart data available")}</p>;

  return (
    <div style={{ minHeight: "290px" }}>
      <Bar data={chartData} width={width} height={height} options={options} />
    </div>
  );
};

export default BloodGroupGenderBarChart;
